import React from "react"
import PropTypes from "prop-types"
import { Button, Flex, Checkbox, Collapse, Spin } from "antd"
import styles from "./index.module.css"
import { useTranslation } from "react-i18next"
import FilterBadge from "components/FilterBadge"

export default function ExperiencesFilterDropdown({
  value = [],
  onChange,
  updateSearchFilters,
  categories = [],
  selectedSubcategories = [],
  onSubcategoryChange,
  subcategoriesByCategory = {},
  onExpandCategory
}) {
  const { t } = useTranslation()

  const handleCategorySelect = (category) => {
    const isSelected = value.includes(category.id)
    const newValue = isSelected ? value.filter((id) => id !== category.id) : [...value, category.id]
    onChange(newValue)
    updateSearchFilters?.({ category_id: newValue.length > 0 ? newValue.join(", ") : "" })

    // Deselecting a category clears its subcategory selections so filters stay in sync
    if (isSelected) {
      const categorySubIds = (subcategoriesByCategory[category.id]?.data || []).map((sub) => sub.id)
      if (categorySubIds.length > 0) {
        updateSubcategories(selectedSubcategories.filter((id) => !categorySubIds.includes(id)))
      }
    }
  }

  const updateSubcategories = (newIds) => {
    onSubcategoryChange?.(newIds)
    updateSearchFilters?.({ subcategory_id: newIds.length > 0 ? newIds.join(", ") : "" })
  }

  const handleSubcategorySelect = (subId, categoryId) => {
    const isRemoving = selectedSubcategories.includes(subId)
    const newIds = isRemoving ? selectedSubcategories.filter((id) => id !== subId) : [...selectedSubcategories, subId]
    updateSubcategories(newIds)

    // Auto-select parent category when a subcategory is picked
    if (!isRemoving && !value.includes(categoryId)) {
      const newCategories = [...value, categoryId]
      onChange(newCategories)
      updateSearchFilters?.({ category_id: newCategories.join(", ") })
    }
  }

  const handleExpand = (activeKeys) => {
    const openKey = Array.isArray(activeKeys) ? activeKeys.at(-1) : activeKeys
    if (openKey != null && openKey !== "") onExpandCategory?.(openKey)
  }

  const handleClear = () => {
    onChange([])
    updateSubcategories([])
    updateSearchFilters?.({ category_id: "" })
  }

  const selectedSubCount = (categoryId) =>
    (subcategoriesByCategory[categoryId]?.data || []).filter((sub) => selectedSubcategories.includes(sub.id)).length

  const renderSubcategories = (categoryState, categoryId) => {
    if (categoryState?.loading) {
      return (
        <Flex justify="center" className={styles.subcategoriesLoading}>
          <Spin size="small" />
        </Flex>
      )
    }
    const subcategories = categoryState?.data || []
    if (subcategories.length === 0) {
      return <div className={styles.noExperiencesFound}>{t("filterTags.no_subcategories_found")}</div>
    }
    return (
      <Flex gap={8} wrap="wrap">
        {subcategories.map((sub) => (
          <Button
            key={sub.id}
            type={selectedSubcategories.includes(sub.id) ? "primary" : "default"}
            shape="round"
            size="small"
            onClick={() => handleSubcategorySelect(sub.id, categoryId)}
          >
            {sub.name}
          </Button>
        ))}
      </Flex>
    )
  }

  const collapseItems = categories.map((category) => ({
    key: String(category.id),
    label: (
      <Flex align="center" gap={8} className={styles.accordionHeader}>
        <Checkbox
          checked={value.includes(category.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => handleCategorySelect(category)}
        />
        <span>{category.name}</span>
        <FilterBadge count={selectedSubCount(category.id)} style={{ marginLeft: "auto" }} />
      </Flex>
    ),
    children: renderSubcategories(subcategoriesByCategory[category.id], category.id)
  }))

  return (
    <div className={styles.dropdownContent}>
      <div className={styles.categoriesWrapper}>
        {categories.length > 0 ? (
          <Collapse accordion ghost items={collapseItems} onChange={handleExpand} expandIconPosition="end" />
        ) : (
          <div className={styles.noExperiencesFound}>{t("filterTags.no_experiences_found")}</div>
        )}
      </div>
      <Button type="link" onClick={handleClear}>
        {t("filterTags.clear")}
      </Button>
    </div>
  )
}

ExperiencesFilterDropdown.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  updateSearchFilters: PropTypes.func,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    })
  ),
  selectedSubcategories: PropTypes.array,
  onSubcategoryChange: PropTypes.func,
  subcategoriesByCategory: PropTypes.object,
  onExpandCategory: PropTypes.func
}
